// src/store/api/customersApi.ts
import type {UpdateUserDto} from "../../types";
import type {
  PaginatedUserResponseDto,
  UserFilterDto,
  UserResponseDto,
  UserStatsDto,
} from "../../types/users";
import {baseApi} from "./baseApi";

export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get Customers (USER role only)
    getCustomers: builder.query<PaginatedUserResponseDto, UserFilterDto>({
      query: (params) => ({
        url: "/users/customers",
        params: {...params, role: "CUSTOMER"}, // Force USER role filter
      }),
      transformResponse: (response: {data: PaginatedUserResponseDto}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({type: "User" as const, id})),
              {type: "User", id: "CUSTOMER_LIST"},
            ]
          : [{type: "User", id: "CUSTOMER_LIST"}],
    }),

    // Get Customer by ID
    getCustomerById: builder.query<UserResponseDto, number>({
      query: (id) => `/users/customers/${id}`,
      transformResponse: (response: {data: UserResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "User", id}],
    }),

    // // Create Customer
    // createCustomer: builder.mutation<UserResponseDto, CreateUserDto>({
    //   query: (data) => ({
    //     url: "/users/customers",
    //     method: "POST",
    //     body: {...data, role: "USER"}, // Force USER role
    //   }),
    //   invalidatesTags: [
    //     {type: "User", id: "CUSTOMER_LIST"},
    //     {type: "User", id: "LIST"}, // Also invalidate general user list
    //   ],
    // }),

    // Update Customer
    updateCustomer: builder.mutation<
      UserResponseDto,
      {id: number; data: Omit<UpdateUserDto, "role">} // Remove role from customer updates
    >({
      query: ({id, data}) => ({
        url: `/users/customers/${id}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: {data: UserResponseDto}) => response.data,
      invalidatesTags: (result, error, {id}) => [
        {type: "User", id},
        {type: "User", id: "CUSTOMER_LIST"},
        {type: "User", id: "LIST"}, // Also invalidate general user list
      ],
    }),

    // Delete Customer
    deleteCustomer: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/users/customers/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: any) => response?.data || response,
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "CUSTOMER_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),

    // Get Customer Stats
    getCustomerStats: builder.query<UserStatsDto, void>({
      query: () => "/users/customers/stats",
      providesTags: [{type: "User", id: "CUSTOMER_STATS"}],
    }),

    // Verify Customer Email
    verifyCustomerEmail: builder.mutation<UserResponseDto, number>({
      query: (id) => ({
        url: `/users/customers/${id}/verify-email`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "CUSTOMER_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),

    // Bulk Delete Customers
    bulkDeleteCustomers: builder.mutation<
      {success: boolean; message: string},
      number[]
    >({
      query: (ids) => ({
        url: "/users/customers/bulk-delete",
        method: "DELETE",
        body: {ids},
      }),
      invalidatesTags: [
        {type: "User", id: "CUSTOMER_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomerByIdQuery,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerStatsQuery,
  useVerifyCustomerEmailMutation,
  useBulkDeleteCustomersMutation,
} = customerApi;
